import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { PasswordInput } from '../components/Auth/PasswordInput'
import { Key, ArrowLeft, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

export const PasswordReset: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { updatePassword, loading, error, clearError } = useAuth()
  
  // Form state
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmNewPassword: ''
  })
  
  // UI states
  const [passwordErrors, setPasswordErrors] = useState<{[key: string]: string}>({})
  const [resetLoading, setResetLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [tokenValid, setTokenValid] = useState(true)

  // Check if we have valid reset parameters or if user is in recovery session
  useEffect(() => {
    const token = searchParams.get('token')
    const type = searchParams.get('type')
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')
    
    // If there's an error in the URL, show invalid token
    if (error) {
      console.error('Password reset error:', error, errorDescription)
      setTokenValid(false)
      return
    }
    
    // If we have token and type=recovery, we're good
    if (token && type === 'recovery') {
      setTokenValid(true)
      return
    }
    
    // If no token parameters, check if user has an active session (Supabase processed the token)
    // This handles the case where Supabase redirects after processing the reset token
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session && session.user) {
          // User has a valid session, likely from password reset flow
          setTokenValid(true)
        } else {
          setTokenValid(false)
        }
      } catch (err) {
        console.error('Session check error:', err)
        setTokenValid(false)
      }
    }
    
    checkSession()
  }, [searchParams])

  // Clear messages after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('')
        navigate('/chat') // Redirect to chat after success
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage, navigate])

  // Clear auth error when user types
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000)
      return () => clearTimeout(timer)
    }
  }, [error, clearError])

  // Handle input changes
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
    
    // Clear field error
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }))
    }
    
    // Clear auth error
    if (error) {
      clearError()
    }
  }

  // Validation
  const validatePassword = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = 'New password is required'
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters'
    }

    if (!passwordData.confirmNewPassword.trim()) {
      errors.confirmNewPassword = 'Please confirm your new password'
    } else if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      errors.confirmNewPassword = 'Passwords do not match'
    }

    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validatePassword()) {
      return
    }

    setResetLoading(true)
    
    try {
      const { error } = await updatePassword({ password: passwordData.newPassword })
      
      if (error) {
        console.error('Password reset error:', error)
      } else {
        setSuccessMessage('Password updated successfully! Redirecting to chat...')
      }
    } catch (err) {
      console.error('Password reset exception:', err)
    } finally {
      setResetLoading(false)
    }
  }

  // Invalid token view
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center p-4">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary-600/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-primary-500/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md text-center">
          <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-lg border border-dark-tertiary/30 rounded-2xl p-8 shadow-xl">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-2xl mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-light-primary mb-4">Invalid Reset Link</h1>
            <p className="text-light-tertiary mb-6">
              This password reset link is invalid or has expired. Please request a new password reset from your account settings.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl focus:ring-2 focus:ring-primary-500/50"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Chat</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary-600/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-primary-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-500 rounded-2xl mb-4 shadow-lg">
            <Key className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-light-primary mb-2">
            Reset Password
          </h1>
          <p className="text-light-tertiary">
            Enter your new password below
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-400 text-sm">{successMessage}</p>
          </div>
        )}

        {/* Auth Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Password Reset Form */}
        <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-lg border border-dark-tertiary/30 rounded-2xl p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-light-secondary mb-2">
                New Password
              </label>
              <PasswordInput
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter your new password"
                autoComplete="new-password"
                error={!!passwordErrors.newPassword}
              />
              {passwordErrors.newPassword && (
                <p className="mt-1 text-sm text-red-400">{passwordErrors.newPassword}</p>
              )}
              <p className="mt-1 text-xs text-light-tertiary">Must be at least 8 characters long</p>
            </div>

            {/* Confirm New Password */}
            <div>
              <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-light-secondary mb-2">
                Confirm New Password
              </label>
              <PasswordInput
                id="confirmNewPassword"
                name="confirmNewPassword"
                value={passwordData.confirmNewPassword}
                onChange={handlePasswordChange}
                placeholder="Confirm your new password"
                autoComplete="new-password"
                error={!!passwordErrors.confirmNewPassword}
              />
              {passwordErrors.confirmNewPassword && (
                <p className="mt-1 text-sm text-red-400">{passwordErrors.confirmNewPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={resetLoading || loading}
              className="
                flex items-center justify-center space-x-2 w-full px-6 py-3
                bg-primary-500 hover:bg-primary-600 disabled:bg-dark-tertiary
                text-white rounded-lg transition-all duration-200
                disabled:cursor-not-allowed disabled:text-light-tertiary
                shadow-lg hover:shadow-xl focus:ring-2 focus:ring-primary-500/50
              "
            >
              {resetLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{resetLoading ? 'Updating Password...' : 'Update Password'}</span>
            </button>
          </form>

          {/* Back to Sign In */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/signin')}
              className="text-primary-400 hover:text-primary-300 text-sm transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-primary-500/50 rounded"
            >
              Back to Sign In
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="text-xs text-light-tertiary/70 space-y-1">
            <p>© 2024 Listening Glass, LLC. All rights reserved.</p>
            <p>The Beneficiary Liquidity Plan (BLP) is a registered trademark and used with permission of Phil Graham, creator of the BLP.</p>
          </div>
        </div>
      </div>
    </div>
  )
}